import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coffee, Sun, Sandwich, Moon, LucideIcon } from "lucide-react";

interface MealConfig {
  name: string;
  time: string;
  icon: LucideIcon;
  enabled: boolean;
}

interface PlanConfigFormProps {
  onSubmit: (config: {
    meals: { name: string; time: string }[];
    objective: string;
    customObjective?: string;
    notes?: string;
    daysCount: number;
  }) => void;
  loading?: boolean;
}

export function PlanConfigForm({ onSubmit, loading }: PlanConfigFormProps) {
  const [meals, setMeals] = useState<MealConfig[]>([
    { name: "Café da manhã", time: "07:00", icon: Coffee, enabled: true },
    { name: "Almoço", time: "12:00", icon: Sun, enabled: true },
    { name: "Lanche da tarde", time: "16:00", icon: Sandwich, enabled: false },
    { name: "Jantar", time: "19:00", icon: Moon, enabled: true },
  ]);

  const [objective, setObjective] = useState("maintenance");
  const [customObjective, setCustomObjective] = useState("");
  const [notes, setNotes] = useState("");
  const [daysCount, setDaysCount] = useState(7);

  const handleMealToggle = (index: number) => {
    const newMeals = [...meals];
    newMeals[index].enabled = !newMeals[index].enabled;
    setMeals(newMeals);
  };

  const handleMealTimeChange = (index: number, time: string) => {
    const newMeals = [...meals];
    newMeals[index].time = time;
    setMeals(newMeals);
  };

  const handleSubmit = () => {
    const selectedMeals = meals
      .filter((m) => m.enabled)
      .map((m) => ({ name: m.name, time: m.time }));

    if (selectedMeals.length === 0) {
      return;
    }

    onSubmit({
      meals: selectedMeals,
      objective,
      customObjective: objective === "custom" ? customObjective : undefined,
      notes,
      daysCount,
    });
  };

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🍽️ Configurar Plano Alimentar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Duração do Plano */}
        <div className="space-y-2">
          <Label>Duração do Plano</Label>
          <Select value={daysCount.toString()} onValueChange={(v) => setDaysCount(parseInt(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 dia</SelectItem>
              <SelectItem value="3">3 dias</SelectItem>
              <SelectItem value="7">7 dias (1 semana)</SelectItem>
              <SelectItem value="14">14 dias (2 semanas)</SelectItem>
              <SelectItem value="30">30 dias (1 mês)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Refeições */}
        <div className="space-y-3">
          <Label className="text-base">Refeições Diárias (máx. 4)</Label>
          {meals.map((meal, index) => (
            <div key={index} className="flex items-center gap-3 p-3 rounded-lg border border-green-200 bg-white">
              <Checkbox
                checked={meal.enabled}
                onCheckedChange={() => handleMealToggle(index)}
              />
              <meal.icon className="h-5 w-5 text-green-600" />
              <span className="flex-1 font-medium">{meal.name}</span>
              <Input
                type="time"
                value={meal.time}
                onChange={(e) => handleMealTimeChange(index, e.target.value)}
                disabled={!meal.enabled}
                className="w-32"
              />
            </div>
          ))}
          <p className="text-sm text-muted-foreground">
            Selecione as refeições que farão parte do plano
          </p>
        </div>

        {/* Objetivos */}
        <div className="space-y-2">
          <Label htmlFor="objective" className="text-base">
            Objetivo Principal
          </Label>
          <Select value={objective} onValueChange={setObjective}>
            <SelectTrigger id="objective">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weight_loss">🎯 Emagrecer</SelectItem>
              <SelectItem value="muscle_gain">💪 Ganho de Massa</SelectItem>
              <SelectItem value="maintenance">⚖️ Manutenção</SelectItem>
              <SelectItem value="specific_restriction">⚠️ Restrição Específica</SelectItem>
              <SelectItem value="custom">✏️ Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Campo customizado para objetivo personalizado ou restrição */}
        {(objective === "custom" || objective === "specific_restriction") && (
          <div className="space-y-2">
            <Label htmlFor="customObjective">
              {objective === "custom" ? "Descreva o objetivo personalizado" : "Descreva a restrição específica"}
            </Label>
            <Textarea
              id="customObjective"
              value={customObjective}
              onChange={(e) => setCustomObjective(e.target.value)}
              placeholder={objective === "custom"
                ? "Ex: Melhorar performance esportiva, controlar diabetes..."
                : "Ex: Intolerância à lactose, alergia a frutos do mar..."
              }
              rows={3}
            />
          </div>
        )}

        {/* Observações Adicionais */}
        <div className="space-y-2">
          <Label htmlFor="notes">Observações Adicionais (opcional)</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Preferências alimentares, horários especiais, etc."
            rows={3}
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={loading || meals.filter((m) => m.enabled).length === 0}
          className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600"
          size="lg"
        >
          {loading ? "Gerando..." : "🤖 Gerar Plano Alimentar com IA"}
        </Button>
      </CardContent>
    </Card>
  );
}
